/**
 * The demo domain, loaded when something actually needs it.
 *
 * `generated.ts` is the transitive closure of the demo's own logic — some
 * 37,000 lines. Four modules that are always mounted used to import it
 * directly: the theme switch, the gateway's audit call, the header toolbar and
 * the explain listeners. Each is a handful of functions, and each one dragged
 * the whole slice into the entry chunk, so opening the work-orders page
 * downloaded the entire aerodrome platform first.
 *
 * They go through here instead. The import is memoised, so the tenth caller
 * gets the same module the first one did, and a demo screen mounting warms it
 * for everything else.
 *
 * The screens themselves still import it statically — they are lazy routes, so
 * their chunk is the right place for it.
 */
import { config } from "../../config/env";
import { FEEDS } from "./feeds";

type Demo = typeof import("./generated");

/*
 * Feeds this platform answers itself, which the demonstration must not speak
 * over. The stores board derives its figures from the register held here
 * (domain/storesFeed.ts) and marks them as records rather than as a feed;
 * filling the slot with an invented stock payload would replace a real
 * reading with a made-up one, which is the opposite of the point.
 */
const ANSWERED_HERE = new Set(["inventory"]);

/**
 * Put the demonstration's figures on the boards that have no other source.
 *
 * Without this, a dozen screens — sustainability, stakeholders, passenger
 * experience, revenue — open empty, print the data shape they expect and
 * wait. That is honest and it reads as unfinished: somebody finds the
 * DEMONSTRATION FEEDS button on the dynamic dashboard, or they never see
 * what the screen does at all.
 *
 * Two rules keep it honest instead of merely full.
 *
 * Only feeds nothing else answers are filled. A feed the platform derives
 * from its own records is left exactly as it was — `demoFeeds` pushes a
 * payload into every slot it knows, so what it would have overwritten is
 * taken back afterwards rather than trusted not to be touched.
 *
 * And only when no platform is connected. Point this copy at a real
 * platform and the airport's own figures are the ones that matter; inventing
 * numbers beside them would be indefensible.
 *
 * Every tile filled this way carries a DEMONSTRATION badge, which is what
 * makes showing them acceptable at all.
 */
function showDemonstrationFigures(m: Demo): void {
  if (config.apiUrl) return;
  const feeds = FEEDS as Record<string, { state: string; data: unknown; src?: string }>;
  const held = Object.keys(feeds)
    .filter((k) => ANSWERED_HERE.has(k) || feeds[k].state === "partial" || feeds[k].state === "live")
    .map((k) => [k, { state: feeds[k].state, data: feeds[k].data, src: feeds[k].src }] as const);
  m.demoFeeds();
  for (const [k, prev] of held) Object.assign(feeds[k], prev);
}

let pending: Promise<Demo> | null = null;

let loaded: Demo | null = null;

/** Load it, or hand back the load already in flight. */
export function loadDemo(): Promise<Demo> {
  if (!pending)
    pending = import("./generated").then((m) => {
      loaded = m;
      /*
       * Catch up on the view that was chosen before it arrived. The theme
       * switch cannot push into a module that is not there, so the module asks
       * on the way in — otherwise a session that opened in daylight gets a
       * demo screen still drawn for a dark room.
       */
      m.setLightView(document.body.classList.contains("lightview"));
      /* before anything queued draws, so nothing paints an empty board first */
      showDemonstrationFigures(m);
      for (const fn of waiting.splice(0)) fn(m);
      return m;
    });
  return pending;
}

/**
 * The module if it is already here, otherwise null.
 *
 * For callers that must not cause the load: the theme switch runs on every
 * mount, and reaching for the domain there would drag three megabytes into the
 * first page whether or not anything needed it.
 */
export const demoIfLoaded = (): Demo | null => loaded;

const waiting: ((d: Demo) => void)[] = [];

/**
 * Run something once the domain is here — WITHOUT asking for it.
 *
 * The difference from `withDemo` is the whole point of the split: this never
 * triggers the load. It is for work that only matters when the platform's own
 * screens are in use — filling the facilities bar, writing to the session
 * activity log — where causing a three megabyte download from the sign-in
 * screen, or from a page that never shows either, would be absurd.
 *
 * Anything queued here runs in order when the domain does arrive.
 */
export function whenDemoLoaded(fn: (d: Demo) => void): void {
  if (loaded) {
    fn(loaded);
    return;
  }
  waiting.push(fn);
}

/**
 * Run something against the demo domain, now or as soon as it arrives.
 *
 * For callers who do not care when it happens — a theme change repainting a
 * canvas, an action being written to the audit trail. A caller that needs the
 * result should await `loadDemo()` itself.
 */
export function withDemo(fn: (d: Demo) => void): void {
  void loadDemo().then(fn);
}
